import PropTypes from 'prop-types';
import React from 'react';
import styled from 'styled-components';

import Select from 'components/Select';

import {
  COLOR_GRAY,
  COLOR_LIGHTGRAY,
  COLOR_BLACK,
  COLOR_PRIMARY,
  em,
} from '@ontruck/ontruck-react-components/lib/vars';

import Icon from '@ontruck/ontruck-react-components/lib/Icon';

export const CustomSelect = styled(Select)`
  ${(props) =>
    props.invalid
      ? `
    .Select-control{
      border-color: var(--orangePeach);
    }
    .Select-placeholder{
      background-color: var(--yellowBanana);
    }
  `
      : null}

  width: 350px;

  .Select-control {
    display: flex;
    align-items: center;
  }

  &.has-value .Select-control {
    background-color: #fff;
  }

  &.is-focused.is-open {
    .Select-placeholder,
    .Select-value {
      display: none;
    }
  }

  .Select-multi-value-wrapper {
    flex: 1;
    width: 100%;
  }

  .Select-arrow-zone {
    display: block;
    width: 38px;
    padding-right: 5px;
  }

  .Select-noresults {
    padding: 0;
  }

  .Select-value {
    padding: 0;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
  }
  .Select-menu-outer {
    background-color: #fff;
    max-height: 240px;
    .Select-menu {
      max-height: 240px;
    }

    *::-webkit-scrollbar {
      width: 6px !important;
      height: 6px !important;
    }

    *::-webkit-scrollbar-thumb {
      background-color: rgba(0, 0, 0, 0.5);
    }

    *::-webkit-scrollbar-track {
      background: rgba(255, 255, 255, 0.8);
    }
  }
`;

const ItemOption = styled.div`
  display: flex;
  width: 100%;
  position: relative;
  padding: 15px;
  align-items: center;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: left;
  cursor: pointer;
  border-bottom: 1px solid ${COLOR_GRAY};
  &:last-child {
    border-bottom: 0;
  }

  & > div {
    display: flex;
    flex-direction: column;
    width: 100%;
    overflow: hidden;
  }

  &:before {
    font-family: 'icontruck', serif;
    content: '';
    min-height: 24px;
    min-width: 24px;
    flex: 0 24px;
    border-radius: 24px;
    display: block;
    border: 1px solid ${COLOR_GRAY};
    background-color: ${COLOR_LIGHTGRAY};
    width: 24px;
    height: 24px;
    text-align: center;
    line-height: 24px;
    margin-right: 15px;
  }

  ${(props) =>
    props.isFocused
      ? `
    background-color: #EFF7E7;
    color: ${COLOR_BLACK}
    &:before{
      border-color: #67B70F;
    }
  `
      : null}

  ${(props) =>
    props.isSelected
      ? `
    background-color: #EFF7E7;
    &:before{
      background-color: #67B70F;
      border-color: #67B70F;
      content: "\\e128";
      color: #fff;
    }
  `
      : null}
`;

const ItemValue = styled.div`
  display: flex;
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  padding: 0 45px 0 15px;
  align-items: center;
`;

const Label = styled.div`
  white-space: nowrap;
  margin-right: 5px;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const LabelHelp = styled.div`
  color: ${COLOR_GRAY};
  white-space: nowrap;
  font-size: ${em(14)};
`;

const IconSearchContainer = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 6px;
  font-size: ${em(20)};
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid ${COLOR_GRAY};
`;

const IconSearch = styled(Icon)``;

const NewItemOption = styled.div`
  background-color: ${COLOR_LIGHTGRAY};
  color: ${COLOR_PRIMARY};
  padding: 10px;
  border-bottom: 1px solid ${COLOR_GRAY};
  width: 100%;
  cursor: pointer;
  &:last-child {
    border-bottom: 0;
  }
  &:hover {
    text-decoration: underline;
  }
`;

export const ValueRenderer = (value) => {
  const { company_name, zipcode, city } = value || {};

  return (
    <ItemValue>
      <Label>{company_name}</Label>
      <LabelHelp>- {`${zipcode} ${city}`}</LabelHelp>
    </ItemValue>
  );
};

ValueRenderer.propTypes = {
  visibleLabel: PropTypes.any,
  labelHelp: PropTypes.any,
};

export const OptionComponent = ({ option, onSelect, onFocus, isFocused, isSelected }) => {
  const { action, label, visibleLabel, labelHelp } = option;

  const handleMouseDown = (event) => {
    event.preventDefault();
    event.stopPropagation();
    onSelect(option, event);
  };
  const handleMouseEnter = (event) => {
    onFocus(option, event);
  };
  const handleMouseMove = (event) => {
    if (isFocused) return;
    onFocus(option, event);
  };

  return !action ? (
    <ItemOption
      onMouseDown={handleMouseDown}
      onMouseEnter={handleMouseEnter}
      onMouseMove={handleMouseMove}
      isSelected={isSelected}
      isFocused={isFocused}
    >
      <div>
        <Label>{visibleLabel}</Label>
        <LabelHelp>{labelHelp}</LabelHelp>
      </div>
    </ItemOption>
  ) : (
    <NewItemOption
      onMouseDown={handleMouseDown}
      onMouseEnter={handleMouseEnter}
      onMouseMove={handleMouseMove}
      isSelected={isSelected}
      isFocused={isFocused}
    >
      {label}
    </NewItemOption>
  );
};

OptionComponent.propTypes = {
  option: PropTypes.any,
  onSelect: PropTypes.func,
  onFocus: PropTypes.func,
  isFocused: PropTypes.bool,
  isSelected: PropTypes.bool,
};

export const IconRenderer = () => {
  return (
    <IconSearchContainer>
      <IconSearch name="search" />
    </IconSearchContainer>
  );
};

const AddButton = styled.button`
  border: 0;
  color: ${COLOR_PRIMARY};
  padding: 10px;
  background-color: ${COLOR_LIGHTGRAY};
  width: 100%;
  text-align: left;
  border-bottom: 1px solid ${COLOR_GRAY};
`;

const NoResults = styled.div`
  padding: 10px;
`;

export const NoResultsComponent = ({ text, onAddClick, addText }) => {
  return (
    <div>
      <div>
        <AddButton onClick={onAddClick} type="button">
          {addText}
        </AddButton>
      </div>
      <NoResults>{text}</NoResults>
    </div>
  );
};

NoResultsComponent.propTypes = {
  text: PropTypes.string,
  addText: PropTypes.string,
  onAddClick: PropTypes.func,
};
